import { DispatchDeliveryOrderResponseDto } from '@/api/portal'

// Status groups for statistics (matching filter options)
export const statusGroups = {
    new: [DispatchDeliveryOrderResponseDto.status.NEW, DispatchDeliveryOrderResponseDto.status.NEW_ACCEPTED],
    onHold: [
        DispatchDeliveryOrderResponseDto.status.HOLD_LOADED,
        DispatchDeliveryOrderResponseDto.status.HOLD_ARRIVED,
        DispatchDeliveryOrderResponseDto.status.HOLD_UNLOADED,
        DispatchDeliveryOrderResponseDto.status.NOT_ON_FILE,
    ],
    released: [
        DispatchDeliveryOrderResponseDto.status.RELEASED_LOADED,
        DispatchDeliveryOrderResponseDto.status.RELEASED_ARRIVED,
        DispatchDeliveryOrderResponseDto.status.RELEASED_UNLOADED,
        DispatchDeliveryOrderResponseDto.status.ON_FILE,
    ],
    readyForDispatch: [DispatchDeliveryOrderResponseDto.status.READY_FOR_DISPATCH],
    dispatched: [
        DispatchDeliveryOrderResponseDto.status.DISPATCHED_PLANNED,
        DispatchDeliveryOrderResponseDto.status.DISPATCHED_ASSIGNED,
        DispatchDeliveryOrderResponseDto.status.DISPATCHED_CONFIRMED,
    ],
    inProgress: [
        DispatchDeliveryOrderResponseDto.status.IN_PROCESS_PICKUP,
        DispatchDeliveryOrderResponseDto.status.COMPLETED_PICKUP,
        DispatchDeliveryOrderResponseDto.status.IN_PROCESS_DELIVERY,
        DispatchDeliveryOrderResponseDto.status.COMPLETED_DELIVERY,
        DispatchDeliveryOrderResponseDto.status.IN_PROCESS_RETURN,
    ],
    completed: [DispatchDeliveryOrderResponseDto.status.COMPLETED],
    cancelled: [DispatchDeliveryOrderResponseDto.status.CANCELLED],
} as const

// Labels for status groups
export const statusGroupLabels: Record<keyof typeof statusGroups, string> = {
    new: 'New',
    onHold: 'On Hold',
    released: 'Released',
    readyForDispatch: 'Ready for Dispatch',
    dispatched: 'Dispatched',
    inProgress: 'In Progress',
    completed: 'Completed',
    cancelled: 'Cancelled',
}

export const shortDdoStatusMapping: Record<DispatchDeliveryOrderResponseDto.status, string> = {
    [DispatchDeliveryOrderResponseDto.status.NEW]: 'New',
    [DispatchDeliveryOrderResponseDto.status.NEW_ACCEPTED]: 'New Accepted',
    [DispatchDeliveryOrderResponseDto.status.NEW_REJECTED]: 'New Rejected',
    [DispatchDeliveryOrderResponseDto.status.HOLD_LOADED]: 'On-vessel',
    [DispatchDeliveryOrderResponseDto.status.HOLD_ARRIVED]: 'Arrived',
    [DispatchDeliveryOrderResponseDto.status.HOLD_UNLOADED]: 'Off-vessel',
    [DispatchDeliveryOrderResponseDto.status.NOT_ON_FILE]: 'Not on File',
    [DispatchDeliveryOrderResponseDto.status.RELEASED_LOADED]: 'On-vessel',
    [DispatchDeliveryOrderResponseDto.status.RELEASED_ARRIVED]: 'Arrived',
    [DispatchDeliveryOrderResponseDto.status.RELEASED_UNLOADED]: 'Off-vessel',
    [DispatchDeliveryOrderResponseDto.status.ON_FILE]: 'On-File',
    [DispatchDeliveryOrderResponseDto.status.READY_FOR_DISPATCH]: 'Ready for Dispatch',
    [DispatchDeliveryOrderResponseDto.status.DISPATCHED_PLANNED]: 'Dispatched',
    [DispatchDeliveryOrderResponseDto.status.DISPATCHED_ASSIGNED]: 'Dispatched',
    [DispatchDeliveryOrderResponseDto.status.DISPATCHED_CONFIRMED]: 'Dispatched',
    [DispatchDeliveryOrderResponseDto.status.IN_PROCESS_PICKUP]: 'Pickup In Progress',
    [DispatchDeliveryOrderResponseDto.status.COMPLETED_PICKUP]: 'Pickup Completed',
    [DispatchDeliveryOrderResponseDto.status.IN_PROCESS_DELIVERY]: 'Delivery In Progress',
    [DispatchDeliveryOrderResponseDto.status.COMPLETED_DELIVERY]: 'Delivery Completed',
    [DispatchDeliveryOrderResponseDto.status.IN_PROCESS_RETURN]: 'Return In Progress',
    [DispatchDeliveryOrderResponseDto.status.COMPLETED]: 'Completed',
    [DispatchDeliveryOrderResponseDto.status.CANCELLED]: 'Cancelled',
}

export const ddoStatusMapping: Record<DispatchDeliveryOrderResponseDto.status, string> = {
    [DispatchDeliveryOrderResponseDto.status.NEW]: 'New',
    [DispatchDeliveryOrderResponseDto.status.NEW_ACCEPTED]: 'New Accepted',
    [DispatchDeliveryOrderResponseDto.status.NEW_REJECTED]: 'New Rejected',
    [DispatchDeliveryOrderResponseDto.status.HOLD_LOADED]: 'On Hold - Loaded on Vessel',
    [DispatchDeliveryOrderResponseDto.status.HOLD_ARRIVED]: 'On Hold - Arrived',
    [DispatchDeliveryOrderResponseDto.status.HOLD_UNLOADED]: 'On Hold - Unloaded from Vessel',
    [DispatchDeliveryOrderResponseDto.status.NOT_ON_FILE]: 'On Hold - Not on File',
    [DispatchDeliveryOrderResponseDto.status.RELEASED_LOADED]: 'Released - On Vessel',
    [DispatchDeliveryOrderResponseDto.status.RELEASED_ARRIVED]: 'Released - Arrived',
    [DispatchDeliveryOrderResponseDto.status.RELEASED_UNLOADED]: 'Released - Off-vessel',
    [DispatchDeliveryOrderResponseDto.status.ON_FILE]: 'Released - On-File',
    [DispatchDeliveryOrderResponseDto.status.READY_FOR_DISPATCH]: 'Ready for Dispatch',
    [DispatchDeliveryOrderResponseDto.status.DISPATCHED_PLANNED]: 'Dispatched',
    [DispatchDeliveryOrderResponseDto.status.DISPATCHED_ASSIGNED]: 'Dispatched',
    [DispatchDeliveryOrderResponseDto.status.DISPATCHED_CONFIRMED]: 'Dispatched',
    [DispatchDeliveryOrderResponseDto.status.IN_PROCESS_PICKUP]: 'Pickup In Progress',
    [DispatchDeliveryOrderResponseDto.status.COMPLETED_PICKUP]: 'Pickup Completed',
    [DispatchDeliveryOrderResponseDto.status.IN_PROCESS_DELIVERY]: 'Delivery In Progress',
    [DispatchDeliveryOrderResponseDto.status.COMPLETED_DELIVERY]: 'Delivery Completed',
    [DispatchDeliveryOrderResponseDto.status.IN_PROCESS_RETURN]: 'Return In Progress',
    [DispatchDeliveryOrderResponseDto.status.COMPLETED]: 'Completed',
    [DispatchDeliveryOrderResponseDto.status.CANCELLED]: 'Cancelled',
}

export const ddoStatusColorMapping: Record<DispatchDeliveryOrderResponseDto.status, string> = {
    [DispatchDeliveryOrderResponseDto.status.NEW]: '#74ABE2',
    [DispatchDeliveryOrderResponseDto.status.NEW_ACCEPTED]: '#74ABE2',
    [DispatchDeliveryOrderResponseDto.status.NEW_REJECTED]: '#BCBEC0',
    [DispatchDeliveryOrderResponseDto.status.HOLD_LOADED]: '#F06A93',
    [DispatchDeliveryOrderResponseDto.status.HOLD_ARRIVED]: '#F06A93',
    [DispatchDeliveryOrderResponseDto.status.HOLD_UNLOADED]: '#F06A93',
    [DispatchDeliveryOrderResponseDto.status.NOT_ON_FILE]: '#F06A93',
    [DispatchDeliveryOrderResponseDto.status.RELEASED_LOADED]: '#F38787',
    [DispatchDeliveryOrderResponseDto.status.RELEASED_ARRIVED]: '#F38787',
    [DispatchDeliveryOrderResponseDto.status.RELEASED_UNLOADED]: '#F38787',
    [DispatchDeliveryOrderResponseDto.status.ON_FILE]: '#F38787',
    [DispatchDeliveryOrderResponseDto.status.READY_FOR_DISPATCH]: '#EF8D5D',
    [DispatchDeliveryOrderResponseDto.status.DISPATCHED_PLANNED]: '#3AB5C2',
    [DispatchDeliveryOrderResponseDto.status.DISPATCHED_ASSIGNED]: '#3AB5C2',
    [DispatchDeliveryOrderResponseDto.status.DISPATCHED_CONFIRMED]: '#3AB5C2',
    [DispatchDeliveryOrderResponseDto.status.IN_PROCESS_PICKUP]: '#6973F6',
    [DispatchDeliveryOrderResponseDto.status.IN_PROCESS_DELIVERY]: '#A97DD8',
    [DispatchDeliveryOrderResponseDto.status.IN_PROCESS_RETURN]: '#3FB68E',
    [DispatchDeliveryOrderResponseDto.status.COMPLETED]: '#4D78A2',
    [DispatchDeliveryOrderResponseDto.status.COMPLETED_DELIVERY]: '#A97DD8',
    [DispatchDeliveryOrderResponseDto.status.COMPLETED_PICKUP]: '#6973F6',
    [DispatchDeliveryOrderResponseDto.status.CANCELLED]: '#8CA2AB',
}

export const ddoStatusDarkerColorMapping: Record<DispatchDeliveryOrderResponseDto.status, string> = {
    [DispatchDeliveryOrderResponseDto.status.NEW]: '#5088C0',
    [DispatchDeliveryOrderResponseDto.status.NEW_ACCEPTED]: '#5088C0',
    [DispatchDeliveryOrderResponseDto.status.NEW_REJECTED]: '#8A8C8E',
    [DispatchDeliveryOrderResponseDto.status.HOLD_LOADED]: '#C04060',
    [DispatchDeliveryOrderResponseDto.status.HOLD_ARRIVED]: '#C04060',
    [DispatchDeliveryOrderResponseDto.status.HOLD_UNLOADED]: '#C04060',
    [DispatchDeliveryOrderResponseDto.status.NOT_ON_FILE]: '#C04060',
    [DispatchDeliveryOrderResponseDto.status.RELEASED_LOADED]: '#C05555',
    [DispatchDeliveryOrderResponseDto.status.RELEASED_ARRIVED]: '#C05555',
    [DispatchDeliveryOrderResponseDto.status.RELEASED_UNLOADED]: '#C05555',
    [DispatchDeliveryOrderResponseDto.status.ON_FILE]: '#C05555',
    [DispatchDeliveryOrderResponseDto.status.READY_FOR_DISPATCH]: '#C06030',
    [DispatchDeliveryOrderResponseDto.status.DISPATCHED_PLANNED]: '#208090',
    [DispatchDeliveryOrderResponseDto.status.DISPATCHED_ASSIGNED]: '#208090',
    [DispatchDeliveryOrderResponseDto.status.DISPATCHED_CONFIRMED]: '#208090',
    [DispatchDeliveryOrderResponseDto.status.IN_PROCESS_PICKUP]: '#3040C0',
    [DispatchDeliveryOrderResponseDto.status.IN_PROCESS_DELIVERY]: '#7050A0',
    [DispatchDeliveryOrderResponseDto.status.IN_PROCESS_RETURN]: '#20805C',
    [DispatchDeliveryOrderResponseDto.status.COMPLETED]: '#2D5070',
    [DispatchDeliveryOrderResponseDto.status.COMPLETED_DELIVERY]: '#7050A0',
    [DispatchDeliveryOrderResponseDto.status.COMPLETED_PICKUP]: '#3040C0',
    [DispatchDeliveryOrderResponseDto.status.CANCELLED]: '#607080',
}
